import { ChangeEvent, useEffect, useRef, useState } from "react";

import { isValidDecimalInput, parseDecimalInput } from "@/shared/lib";
import { TextField } from "@mui/material";
import {
	GridRenderEditCellParams,
	GridValidRowModel,
	useGridApiContext,
} from "@mui/x-data-grid";

interface DecimalEditCellProps<
	R extends GridValidRowModel = GridValidRowModel,
> extends GridRenderEditCellParams<R, number> {
	allowNegative?: boolean;
	fallbackValue?: number;
}

export const DecimalEditCell = <
	R extends GridValidRowModel = GridValidRowModel,
>({
	id,
	field,
	value,
	hasFocus,
	allowNegative = true,
	fallbackValue = 0,
}: DecimalEditCellProps<R>) => {
	const apiRef = useGridApiContext();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [inputValue, setInputValue] = useState(
		value == null ? "" : String(value),
	);

	useEffect(() => {
		if (hasFocus) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [hasFocus]);

	const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const nextValue = event.target.value;

		if (!isValidDecimalInput(nextValue, { allowNegative })) {
			return;
		}

		setInputValue(nextValue);

		await apiRef.current.setEditCellValue(
			{
				id,
				field,
				value: parseDecimalInput(nextValue, { fallbackValue }),
			},
			event,
		);
	};

	return (
		<TextField
			fullWidth
			variant="standard"
			value={inputValue}
			onChange={(event) => {
				void handleChange(event);
			}}
			inputRef={inputRef}
			type="text"
			inputProps={{
				inputMode: "decimal",
				pattern: allowNegative ? "-?[0-9]*[.,]?[0-9]*" : "[0-9]*[.,]?[0-9]*",
			}}
			onWheel={(event) => {
				event.currentTarget.blur();
			}}
		/>
	);
};
